import { GAME_CONFIG } from "../config/gameConfig.js";

/**
 * Room registry & visibility for lighting fog.
 * Target is binary (current room on, others off); visibility fades over time.
 */

/** @type {{ id: number, tileRect: { x: number, y: number, w: number, h: number }, cells?: boolean[][], worldRect: object }[]} */
let rooms = [];

/** @type {Map<number, number>} smoothed 0–1 visibility per room */
let roomVisibility = new Map();

/** World tile key → room id (first paint wins). Speeds wall-bleed checks. */
let tileOwnerByKey = new Map();

function tileKey(tx, ty) {
    return `${tx},${ty}`;
}

function rebuildTileOwnerIndex() {
    tileOwnerByKey.clear();
    for (const room of rooms) {
        const { x, y, w, h } = room.tileRect;
        if (!room.cells) {
            for (let ty = y; ty < y + h; ty++) {
                for (let tx = x; tx < x + w; tx++) {
                    const key = tileKey(tx, ty);
                    if (!tileOwnerByKey.has(key)) tileOwnerByKey.set(key, room.id);
                }
            }
            continue;
        }
        for (let row = 0; row < room.cells.length; row++) {
            const line = room.cells[row];
            for (let col = 0; col < line.length; col++) {
                if (!line[col]) continue;
                const key = tileKey(x + col, y + row);
                if (!tileOwnerByKey.has(key)) tileOwnerByKey.set(key, room.id);
            }
        }
    }
}

function tileToWorldRect(tileRect) {
    const tw = GAME_CONFIG.level.tileWidth;
    const th = GAME_CONFIG.level.tileHeight;
    return {
        x: tileRect.x * tw,
        y: tileRect.y * th,
        w: tileRect.w * tw,
        h: tileRect.h * th,
    };
}

function pointInWorldRect(px, py, wr) {
    return px >= wr.x && px < wr.x + wr.w && py >= wr.y && py < wr.y + wr.h;
}

function pointInRoomCell(px, py, room) {
    if (!room.cells) return pointInWorldRect(px, py, room.worldRect);

    const tw = GAME_CONFIG.level.tileWidth;
    const th = GAME_CONFIG.level.tileHeight;
    const tx = Math.floor(px / tw) - room.tileRect.x;
    const ty = Math.floor(py / th) - room.tileRect.y;

    return Boolean(room.cells[ty]?.[tx]);
}

/** True if tile (tx, ty) belongs to any room other than currentRoomId. */
export function tileInOtherRoom(tx, ty, currentRoomId) {
    const owner = tileOwnerByKey.get(tileKey(tx, ty));
    return owner != null && owner !== currentRoomId;
}

export function clearRooms() {
    rooms = [];
    roomVisibility.clear();
    tileOwnerByKey.clear();
}

/**
 * @param {{ id: number, tileRect: { x: number, y: number, w: number, h: number }, cells?: boolean[][] }[]} list
 */
export function setRooms(list) {
    rooms = (list || []).map((r) => ({
        id: r.id,
        tileRect: { ...r.tileRect },
        cells: r.cells ? r.cells.map((row) => [...row]) : null,
        worldRect: tileToWorldRect(r.tileRect),
        expandedMask: null,
        expandedMaskAllowance: null,
    }));
    roomVisibility.clear();
    rebuildTileOwnerIndex();
}

export function getRooms() {
    return rooms;
}

export function getRoomAt(worldPos) {
    if (!worldPos || rooms.length === 0) return null;
    const px = worldPos.x;
    const py = worldPos.y;
    for (const room of rooms) {
        if (pointInRoomCell(px, py, room)) return room;
    }
    return null;
}

/** Nearest room by distance to AABB (for wall lights outside region paint). */
export function getNearestRoom(worldPos) {
    if (!worldPos || rooms.length === 0) return null;
    const inside = getRoomAt(worldPos);
    if (inside) return inside;

    let best = null;
    let bestDist = Infinity;
    const px = worldPos.x;
    const py = worldPos.y;

    for (const room of rooms) {
        const wr = room.worldRect;
        const nearestX = Math.max(wr.x, Math.min(px, wr.x + wr.w));
        const nearestY = Math.max(wr.y, Math.min(py, wr.y + wr.h));
        const dx = px - nearestX;
        const dy = py - nearestY;
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
            bestDist = dist;
            best = room;
        }
    }
    return best;
}

/** Distance from point to nearest edge of AABB (0 if inside). */
function distToRect(px, py, wr) {
    const nearestX = Math.max(wr.x, Math.min(px, wr.x + wr.w));
    const nearestY = Math.max(wr.y, Math.min(py, wr.y + wr.h));
    if (pointInWorldRect(px, py, wr)) return 0;
    const dx = px - nearestX;
    const dy = py - nearestY;
    return Math.sqrt(dx * dx + dy * dy);
}

function distToRoomCells(px, py, room) {
    if (!room.cells) return distToRect(px, py, room.worldRect);
    if (pointInRoomCell(px, py, room)) return 0;

    const tw = GAME_CONFIG.level.tileWidth;
    const th = GAME_CONFIG.level.tileHeight;
    let best = Infinity;

    for (let row = 0; row < room.cells.length; row++) {
        for (let col = 0; col < room.cells[row].length; col++) {
            if (!room.cells[row][col]) continue;

            const x = (room.tileRect.x + col) * tw;
            const y = (room.tileRect.y + row) * th;
            best = Math.min(best, distToRect(px, py, { x, y, w: tw, h: th }));
        }
    }

    return best;
}

/**
 * Smoothed visibility 0–1 per room.
 * Inside a region → that room targets 1, others 0.
 * In a Regions gap → adjacent rooms target gapAmbient (~25%) so doorways aren't pitch black.
 * @returns {Map<number, number>}
 */
export function getRoomVisibility(playerPos) {
    const result = new Map();
    if (!playerPos || rooms.length === 0) {
        roomVisibility.clear();
        return result;
    }

    const current = getRoomAt(playerPos);
    const currentId = current ? current.id : null;
    const fadeDuration = Math.max(0.01, GAME_CONFIG.rooms?.fadeDuration ?? 0.45);
    const gapAmbient = GAME_CONFIG.rooms?.gapAmbient ?? 0.25;
    const gapDist = GAME_CONFIG.rooms?.gapAdjacentDistance ?? 96;
    const step = Math.min(1, Math.max(0, dt()) / fadeDuration);
    const px = playerPos.x;
    const py = playerPos.y;

    for (const room of rooms) {
        let target = 0;
        if (currentId != null) {
            target = room.id === currentId ? 1 : 0;
        } else if (distToRoomCells(px, py, room) <= gapDist) {
            target = gapAmbient;
        }

        let value = roomVisibility.has(room.id) ? roomVisibility.get(room.id) : 0;
        if (value < target) {
            value = Math.min(target, value + step);
        } else if (value > target) {
            value = Math.max(target, value - step);
        }
        if (value < 0.001) value = 0;
        if (value > 0.999) value = 1;
        roomVisibility.set(room.id, value);
        result.set(room.id, value);
    }

    return result;
}

/**
 * Ambient draw factor from smoothed visibility.
 */
export function getRoomAmbientFactor(roomId, visibility) {
    return visibility.get(roomId) ?? 0;
}
